import { TFunction } from 'i18next';
import React, { ReactNode, useContext, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { DefaultTheme, useTheme } from 'styled-components';

import { InvitedUserModel, UserModel } from '../../../../../typings/api/skymap/rest/v1/.common';
import { sessionStorageUtils } from '../../../utils/session-storage-utils';
import { isDefined } from '../../../utils/variables';
import { useDialog } from '../../hooks/use-dialog';
import { Project, ProjectContext } from '../../state/project-state/project-state';
import { Button } from '../button/button';
import { DataTable, DataTableColumns, DataTableSortType } from '../data-table/data-table';
import { Dialog } from '../dialog/dialog';
import { Icon } from '../icon/icon';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { OverlayLoader } from '../overlay-loader/overlay-loader';
import { ProjectUserTags } from '../project-user-tags/project-user-tags';
import { Stack } from '../stack/stack';
import { TextBox } from '../text-box/text-box';
import { AddProjectUsersDialog } from './add-project-users-dialog';
import { useRemoveProjectAttendeeMutation } from './mutations/remove-project-users';

export type ProjectUserItem = {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  role: 'project_attendee' | 'company_admin' | 'invited_user' | 'invited_company_admin';
  editIcon?: ReactNode;
};

type DispatchEvent =
  | { type: 'remove'; item: ProjectUserItem }
  | { type: 'add'; items: ProjectUserItem[] }
  | { type: 'set'; items: ProjectUserItem[] };

function usersReducer(users: ProjectUserItem[], action: DispatchEvent) {
  switch (action.type) {
    case 'remove':
      return users.filter((u) => u.id !== action.item.id);
    case 'add':
      return [...users, ...action.items];
    case 'set':
      return [...action.items];
    default:
      return users;
  }
}

function mapUserToItem(
  user: UserModel,
  role: 'project_attendee' | 'company_admin',
  theme: DefaultTheme,
): ProjectUserItem {
  return {
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
    email: user.email ?? '',
    // phoneNumber is not part of the group user payload; keep the fallback.
    phoneNumber: (user as { phoneNumber?: string | null }).phoneNumber ?? '-',
    role,
    editIcon: (
      <Icon
        color={theme.color.gray.dark}
        icon={['fad', 'pen']}
        size="lg"
        title={'Redigera'}
        onHoverStyle={{ icon: ['fas', 'pen'] }}
      />
    ),
  };
}

// Invited users have no name yet, so surface them with a translated placeholder.
// The role keeps the scope so the role column reads "... (Not verified)" correctly.
function mapInvitedUserToItem(
  user: InvitedUserModel,
  role: 'invited_user' | 'invited_company_admin',
  t: TFunction,
): ProjectUserItem {
  return {
    id: user.id,
    name: t('userList.addProjectUsersDialog.invitedUser', { ns: 'components' }),
    email: user.email,
    phoneNumber: '',
    role,
    editIcon: undefined,
  };
}

function getUsers(theme: DefaultTheme, t: TFunction, project: Project) {
  return [
    ...project.users.map((user) => mapUserToItem(user, 'project_attendee', theme)),
    ...project.companyAdmins.map((user) => mapUserToItem(user, 'company_admin', theme)),
    ...project.invitedUsers.map((user) => mapInvitedUserToItem(user, 'invited_user', t)),
    ...(project.invitedCompanyAdmins ?? []).map((user) =>
      mapInvitedUserToItem(user, 'invited_company_admin', t),
    ),
  ];
}

const getUserRoleTranslation = (value: ProjectUserItem['role'], t: TFunction) => {
  switch (value) {
    case 'project_attendee':
      return t('roles.projectAttendee', { ns: 'common' });
    case 'invited_user':
      return t('roles.invitedUser', { ns: 'common' });
    case 'invited_company_admin':
      return t('roles.invitedCompanyAdmin', { ns: 'common' });
    case 'company_admin':
      return t('roles.companyAdmin', { ns: 'common' });
  }
};

function getColumns(t: TFunction): DataTableColumns<ProjectUserItem> {
  const columns: DataTableColumns<ProjectUserItem> = {
    name: {
      sortableBy: { type: DataTableSortType.STRING },
      title: t('name', { ns: 'common' }),
      alignment: 'left',
      unfilterable: true,
      shrink: true,
    },
    email: {
      sortableBy: { type: DataTableSortType.STRING },
      title: t('newCompanyAdmin.newUser.form.email', { ns: 'components' }),
      alignment: 'left',
      unfilterable: true,
      shrink: true,
    },
    role: {
      sortableBy: { type: DataTableSortType.STRING },
      title: t('role', { ns: 'common' }),
      formatter: (value: ProjectUserItem['role']) => getUserRoleTranslation(value, t),
      alignment: 'left',
      unfilterable: true,
      shrink: true,
    },
  };

  return columns;
}

function useUserListSearchFilter() {
  const [searchText, setSearchText] = useState(
    sessionStorageUtils.getUserItemOrDefault('userListSearchFilter', ''),
  );

  useEffect(() => {
    sessionStorageUtils.setUserItem('userListSearchFilter', searchText);
  }, [searchText]);

  return { searchText, setSearchText };
}

const searchColumns = ['name', 'email', 'role'] as const;
function filterItems(items: ProjectUserItem[], searchText: string) {
  return items.filter((item) => {
    const searchValue = searchText.trim().toLowerCase();
    return searchColumns.some((x) => (item[x] ?? '').toLowerCase().includes(searchValue));
  });
}

export const UserList = () => {
  const projectContext = useContext(ProjectContext);
  const { project, isAdmin } = projectContext;
  const { searchText, setSearchText } = useUserListSearchFilter();
  const addUsersDialog = useDialog();
  const theme = useTheme();
  const { t } = useTranslation();
  const editUserDialog = useDialog();
  const hasAdminAccess = React.useMemo(() => isAdmin(), [isAdmin]);
  const [item, setItem] = useState<ProjectUserItem>();
  const [items, dispatchItems] = useReducer(usersReducer, []);

  const [filteredItems, setFilteredItems] = useState<ProjectUserItem[]>([]);

  useEffect(() => {
    setFilteredItems(filterItems(items, searchText));
  }, [items, searchText]);

  useEffect(() => {
    if (!isDefined(project)) {
      dispatchItems({ type: 'set', items: [] });
      return;
    }

    dispatchItems({
      type: 'set',
      items: getUsers(theme, t, project),
    });
  }, [project, theme, t]);

  return (
    <>
      <Stack alignItems="baseline" direction="row" justifyContent="space-between" spacing={0.5}>
        <SearchFilterContainer direction="row" spacing={0.5}>
          <TextBox
            placeholder={t('search', { ns: 'common' })}
            value={searchText}
            width={250}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </SearchFilterContainer>

        {hasAdminAccess && (
          <Button color="primary" variant="contained" onClick={() => addUsersDialog.show()}>
            {t('userList.addProjectUsersDialog.title', { ns: 'components' })}
          </Button>
        )}
      </Stack>

      <DataTable
        columns={getColumns(t)}
        fixedLayout={true}
        items={filteredItems}
        pageSize={10}
        tableStyle={{
          rowCell: {
            fontSize: '14px',
          },
          headerCell: {
            fontSize: '14px',
          },
        }}
        onRowClicked={(item) => {
          setItem(item);
          editUserDialog.show();
        }}
      />
      {item &&
        editUserDialog.render(
          <EditUserDialog
            hasAdminAccess={hasAdminAccess}
            user={item}
            onClose={() => editUserDialog.hide()}
            onRemoveUser={(item) => {
              dispatchItems({
                type: 'remove',
                item,
              });
            }}
          />,
        )}
      {addUsersDialog.render(
        <AddProjectUsersDialog
          dialog={addUsersDialog}
          onUsersAdded={(users) => {
            dispatchItems({
              type: 'add',
              items: users,
            });
          }}
        />,
      )}
    </>
  );
};

const EditUserDialog = (props: {
  user: ProjectUserItem;
  onRemoveUser: (item: ProjectUserItem) => void;
  onClose: () => void;
  hasAdminAccess: boolean;
}) => {
  const { t } = useTranslation();
  const canBeRemoved =
    props.hasAdminAccess && ['project_attendee', 'invited_user'].includes(props.user.role);
  const canEditTags = props.hasAdminAccess && 'project_attendee' === props.user.role;

  const {
    removeProjectUser,
    removeProjectUserInvitation,
    buildErrorList,
    pendingProjectUserRemoval,
    pendingProjectUserInvitationRemoval,
  } = useRemoveProjectAttendeeMutation({
    onSuccess: () => {
      props.onClose();
      props.onRemoveUser(props.user);
    },
  });

  const isPending = pendingProjectUserRemoval && pendingProjectUserInvitationRemoval;

  return (
    <Dialog
      closeIcon={true}
      closeOnDimmerClick={true}
      maxHeight="expand"
      width={500}
      onClose={() => {
        if (isPending) {
          return;
        }

        props.onClose();
      }}
    >
      {{
        header: getUserRoleTranslation(props.user.role, t),
        content: (
          <OverlayLoader visible={isPending}>
            <Stack spacing={1}>
              <LabelledContainer fontWeight={600} text={t('name', { ns: 'common' })}>
                {props.user.name}
              </LabelledContainer>
              <LabelledContainer
                fontWeight={600}
                text={t('newCompanyAdmin.newUser.form.email', { ns: 'components' })}
              >
                {props.user.email}
              </LabelledContainer>
              <LabelledContainer
                fontWeight={600}
                text={t('newCompanyAdmin.newUser.form.phone', { ns: 'components' })}
              >
                {props.user.phoneNumber}
              </LabelledContainer>
              {canEditTags && <ProjectUserTags user={props.user} />}
              {buildErrorList()}
            </Stack>
          </OverlayLoader>
        ),
        footer: {
          left: (
            <>
              {canBeRemoved && (
                <Button
                  color="error"
                  leftIcon={{ icon: ['fad', 'trash-alt'] }}
                  loading={isPending}
                  variant="contained"
                  onClick={() => {
                    if (props.user.role === 'project_attendee') {
                      removeProjectUser(props.user.id);
                    } else {
                      removeProjectUserInvitation(props.user.id);
                    }
                  }}
                >
                  {t('userList.removeProjectAttendee', { ns: 'components' })}
                </Button>
              )}
            </>
          ),
          right: (
            <Button loading={isPending} variant="text" onClick={() => props.onClose()}>
              {t('close', { ns: 'common' })}
            </Button>
          ),
        },
      }}
    </Dialog>
  );
};

const SearchFilterContainer = styled(Stack)`
  width: 450px;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;
